import {
  Accordion,
  Button,
  Separator,
  Text,
  Tooltip,
} from '@shared/components';
import { useToast } from '@shared/components/toast';
import { MovementTrackingPauseControl } from '@shared/features/shipment-card/MovementTrackingPauseControl';
import {
  TrackingPauseSource,
  useTriggerTrackingProcedureMutation,
} from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useOrganizationFeatureFlag } from '@shared/hooks/useOrganizationFeatureFlag';

import { FeatureFlagName, ScheduleStop } from 'clerk_common/types';
import { LuPhoneOutgoing } from 'react-icons/lu';

type VoomaTrackProps = {
  shipmentId: string;
  movementId?: string;
  isPaused?: boolean;
  showPauseControl?: boolean;
  onTrackingPausedChange?: () => void;
  stops: ScheduleStop[];
  movementEvents: Array<{
    id: string;
    name: string;
    createdAt: string;
    data?: {
      eventType: string;
      exception?: {
        type: string;
        data: any;
      };
    };
  }>;
};

export function VoomaTrack({
  shipmentId,
  movementId,
  isPaused,
  showPauseControl = false,
  onTrackingPausedChange,
  stops,
  movementEvents,
}: VoomaTrackProps) {
  const mayTrackLoad = useOrganizationFeatureFlag({
    featureFlagName: FeatureFlagName.TRACK_LOADS_IN_SIDEKICK,
  });

  if (!mayTrackLoad) return null;

  const trigger = (
    <Text isHeavy type="body-sm">
      Track
    </Text>
  );
  const children = (
    <div className="flex flex-col gap-2">
      <TrackMovementContents
        shipmentId={shipmentId}
        movementId={movementId}
        isPaused={isPaused}
        showPauseControl={showPauseControl}
        onTrackingPausedChange={onTrackingPausedChange}
        stops={stops}
        movementEvents={movementEvents}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <Separator />
      <Accordion
        sections={[{ trigger, children, startOpen: true }]}
        triggerClassName="h-[36px]"
      />
    </div>
  );
}

type LoadTrackingProps = {
  shipmentId: string;
  movementId?: string;
  isPaused?: boolean;
  showPauseControl?: boolean;
  onTrackingPausedChange?: () => void;
  stops: ScheduleStop[];
  movementEvents: Array<{
    id: string;
    name: string;
    createdAt: string;
    data?: {
      eventType: string;
      exception?: {
        type: string;
        data: any;
      };
    };
  }>;
  onViewTrackingMilestones?: () => void;
};

export function TrackMovementContents({
  shipmentId,
  movementId,
  isPaused,
  showPauseControl = false,
  onTrackingPausedChange,
  stops,
  movementEvents,
  onViewTrackingMilestones,
}: LoadTrackingProps) {
  const [triggerTrackingProcedure, ctx] = useTriggerTrackingProcedureMutation();
  const { loading } = ctx;
  const { sendToast } = useToast();
  const { track } = useAnalytics();

  const groupedEvents = new Map<
    string,
    {
      createdEvent?: (typeof movementEvents)[0];
      resolvedEvent?: (typeof movementEvents)[0];
      externalId: string;
    }
  >();

  movementEvents.forEach((event) => {
    const externalId = event.data?.exception?.data?.externalId;
    if (!externalId) return;

    if (!groupedEvents.has(externalId)) {
      groupedEvents.set(externalId, { externalId });
    }

    const group = groupedEvents.get(externalId)!;
    if (event.data?.eventType === 'EXCEPTION_CREATED') {
      group.createdEvent = event;
    } else if (event.data?.eventType === 'EXCEPTION_RESOLVED') {
      group.resolvedEvent = event;
    }
  });

  const combinedItems = [
    ...stops.map((stop, index) => ({
      type: 'stop' as const,
      id: `stop-${index}`,
      date:
        stop.suggestedDt ||
        stop.appointmentRequestedDt ||
        stop.appointmentConfirmedDt ||
        stop.day,
      data: stop,
    })),
    ...Array.from(groupedEvents.values()).map((group) => ({
      type: 'groupedEvent' as const,
      id: group.externalId,
      date: group.createdEvent?.createdAt || group.resolvedEvent?.createdAt,
      data: group,
    })),
  ];

  const sortedItems = combinedItems.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;

    const dateA = new Date(a.date);
    const dateB = new Date(b.date);

    return dateA.getTime() - dateB.getTime();
  });

  async function triggerCheckCallNow() {
    const input = { shipmentId };
    const res = await triggerTrackingProcedure({ variables: { input } });

    const success = res.data?.triggerTrackingProcedure?.success;
    if (success) {
      track('Clicked Do Check Call Now');
      sendToast('Check call triggered', {
        description: 'A check call has been triggered for this shipment.',
        variant: 'success',
      });
    } else {
      track('Failed to Do Check Call Now');
      sendToast('Failed to trigger check call', {
        description: 'Something went wrong triggering the check call.',
        variant: 'error',
      });
    }
  }

  const isDoCheckCallNowDisabled = loading;

  const formatAddress = (address: ScheduleStop['address']) => {
    const parts = [
      address.addressOne,
      address.city,
      address.state,
      address.zip,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatDateTime = (date?: Date | string) => {
    if (!date) return '';
    // Convert string to Date if needed
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col gap-2 rounded-md bg-gray-100 p-2">
      <div className="flex flex-row items-center justify-end gap-2">
        {onViewTrackingMilestones && (
          <Button
            size="xs"
            variant="secondary"
            onPress={onViewTrackingMilestones}
          >
            View tracking milestones
          </Button>
        )}
        {showPauseControl && movementId !== undefined && (
          <MovementTrackingPauseControl
            movementId={movementId}
            isPaused={isPaused ?? false}
            source={TrackingPauseSource.EXTENSION}
            onPausedChange={onTrackingPausedChange}
          />
        )}
        <Tooltip content={`Trigger a check call to the carrier`}>
          <Button
            size="xs"
            variant="secondary"
            onPress={() => triggerCheckCallNow()}
            isDisabled={isDoCheckCallNowDisabled}
            icon={<LuPhoneOutgoing />}
            iconPosition="right"
            className="flex flex-row items-center gap-1"
          >
            Check call
          </Button>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-2">
        {sortedItems.map((item) => {
          if (item.type === 'stop') {
            const stop = item.data;
            return (
              <div
                key={item.id}
                className="flex flex-col gap-1 rounded border border-gray-200 bg-white p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-xs font-medium text-white`}
                    >
                      {stop.stopType === 'PICKUP' ? 'P' : 'D'}
                    </div>
                    <div className="flex flex-col">
                      <Text isHeavy type="body-xs" className="text-gray-800">
                        {stop.locationLabel}
                      </Text>
                      <Text type="body-xs" className="text-gray-600">
                        {formatAddress(stop.address)}
                      </Text>
                    </div>
                  </div>
                  <div className="flex flex-col items-end text-right">
                    {stop.suggestedDt && (
                      <Text type="body-xs" className="text-gray-600">
                        {formatDateTime(stop.suggestedDt)}
                      </Text>
                    )}
                    {stop.appointmentStatus && (
                      <Text type="body-xs" className="text-gray-500">
                        {stop.appointmentStatus}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (item.type === 'groupedEvent') {
            const groupedEvent = item.data;
            const isResolved = !!groupedEvent.resolvedEvent;
            const event =
              groupedEvent.createdEvent || groupedEvent.resolvedEvent;

            if (!event) return null;

            return (
              <div
                key={item.id}
                className={`${
                  isResolved ? 'bg-green-100' : 'bg-orange-100'
                } ml-5 flex flex-col gap-1 rounded border border-gray-200 p-2`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-gray-100 text-xs font-medium text-gray-700`}
                    >
                      E
                    </div>
                    <div className="flex flex-col">
                      <Text isHeavy type="body-xs" className="text-gray-800">
                        {event.name}
                      </Text>
                      {event.data?.exception && (
                        <Text type="body-xs" className="text-gray-600">
                          {event.data.exception.type}
                        </Text>
                      )}
                      <Text type="body-xs" className="text-gray-500">
                        {isResolved ? 'Resolved' : 'Open'}
                      </Text>
                    </div>
                  </div>
                  <div className="flex flex-col items-end text-right">
                    <Text type="body-xs" className="text-gray-600">
                      {formatDateTime(event.createdAt)}
                    </Text>
                  </div>
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
}
